import {
  createRoleEndPoint,
  getRoleList,
  editRoleTemplate,
  saveRoleTemplate,
  deleteRoleRequestDefinition,
  updateRoleInfo,
  fetchRoleRequestDefinition,
  deleteRoleEndPoint,
  fetchRoleByUser,
  retrieveUserSitesEndPoint,
  addUserToGroupsEndPoint,
  addRoleEndPoint,
  revokeRoleEndPoint,
  getUsersList,
} from "../configuration";
import axiosInstance from "../global/axiosInterceptor";
import { handleApiError } from "../global/utility/Utils";

export const getRoles = async (activeSession) => {
  try {
    const response = await axiosInstance.post(getRoleList(), {
      filter: {
        $selection: {},
      },
    });
    const res = response.data;
    return res;
  } catch (error) {
    const errorMessage = error?.response?.data?.error_description;
    console.error("Error fetching user roles:", error);
    handleApiError(
      "warning",
      errorMessage
        ? errorMessage
        : "Failed to fetch user roles, please reload the page."
    );
    return [];
  }
};

export const editRoles = async (activeSession, resourceIdentifier) => {
  try {
    const response = await axiosInstance.post(editRoleTemplate(), {
      resourceidentifier: {
        id: resourceIdentifier,
      },
    });
    const res = response.data;
    return res;
  } catch (error) {
    return null;
  }
};

export const saveRoles = async (activeSession, resourceIdentifier, hrefs) => {
  try {
    const response = await axiosInstance.post(saveRoleTemplate(), {
      resourceidentifier: {
        id: resourceIdentifier.id,
      },
      hrefs: hrefs,
    });
    const res = response.data;
    if (
      resourceIdentifier.id ===
      JSON.parse(sessionStorage.getItem("rolePermissionsList")).id
    )
      sessionStorage.setItem("rolePermissionsList", JSON.stringify(res));
    return res;
  } catch (error) {
    return null;
  }
};

export const deleteRolePermissions = async (
  activeSession,
  resourceIdentifier,
  hrefs
) => {
  try {
    const response = await axiosInstance.post(deleteRoleRequestDefinition(), {
      resourceidentifier: {
        id: resourceIdentifier.id,
      },
      hrefs: hrefs,
    });
    const res = response.data;
    if (
      resourceIdentifier.id ===
      JSON.parse(sessionStorage.getItem("rolePermissionsList")).id
    )
      sessionStorage.setItem("rolePermissionsList", JSON.stringify(res));
    return res;
  } catch (error) {
    return null;
  }
};

export const updateRoleData = async (
  activeSession,
  resourceIdentifier,
  roleInfo
) => {
  try {
    const response = await axiosInstance.patch(updateRoleInfo(), {
      resourceidentifier: {
        id: resourceIdentifier.id,
      },
      role: roleInfo,
    });
    const res = response.data;
    return res;
  } catch (error) {
    return null;
  }
};

export const createRole = async (
  activeSession,
  name,
  description,
  requestdefinitions
) => {
  try {
    const response = await axiosInstance.post(createRoleEndPoint(), {
      role: {
        name: name,
        description: description,
        hasmutablepolicy: true,
        requestdefinitions: requestdefinitions,
      },
    });
    const res = response.data;
    return res;
  } catch (e) {
    return null;
  }
};

export const fetchRequestDefinition = async (activeSession) => {
  try {
    const response = await axiosInstance.get(fetchRoleRequestDefinition());
    const res = response.data;
    return res;
  } catch (error) {
    return null;
  }
};

export const deleteRole = async (activeSession, resourceIdentifier) => {
  try {
    const response = await axiosInstance.delete(deleteRoleEndPoint(), {
      resourceidentifier: {
        id: resourceIdentifier.id,
      },
    });
    const res = response.data;
    return res;
  } catch (error) {
    return null;
  }
};

export const getRoleByUser = async (activeSession, userid, siteid) => {
  try {
    const response = await axiosInstance.post(fetchRoleByUser(), {
      accountname: activeSession.accountId,
      grant: {
        userid: userid,
        siteid: siteid,
      },
    });
    const res = response.data;
    return res;
  } catch (e) {
    const errorMessage = e?.response?.data?.error_description;
    console.error("Error fetching user's role:", errorMessage || e?.message);
    handleApiError(
      "warning",
      errorMessage
        ? errorMessage
        : "Failed to fetch user's role, please reload the page."
    );
    return null; // null = API failure (distinguish from [] = user has no role assigned)
  }
};

export const getUserSites = async (activeSession, payload) => {
  try {
    const response = await axiosInstance.post(
      retrieveUserSitesEndPoint(),
      payload
    );
    const res = response.data;
    return res;
  } catch (e) {
    const errorMessage = e?.response?.data?.error_description;
    console.error("Error fetching user sites:", e);
    handleApiError(
      "warning",
      errorMessage
        ? errorMessage
        : "Failed to fetch user sites, please reload the page."
    );
    return [];
  }
};

export const updateUserToGroups = async (activeSession, payload) => {
  try {
    const response = await axiosInstance.post(
      addUserToGroupsEndPoint(),
      payload
    );
    const res = response.data;
    return res;
  } catch (e) {
    const errorMessage = e?.response?.data?.error_description;
    console.error("Error updating user details:", e);
    handleApiError(
      "warning",
      errorMessage
        ? errorMessage
        : "Failed to update user details, please try again."
    );
    return e?.response || { status: 500, data: { error_description: "An unexpected error occurred" } };
  }
};

export const addRole = async (
  activeSession,
  userid,
  roleid,
  siteids = [],
  fromSites
) => {
  try {
    const response = await axiosInstance.post(addRoleEndPoint(), {
      accountname: activeSession.accountId,
      grant: {
        userid: userid,
        roleid: roleid,
        siteids: siteids,
      },
    });
    const res = response.data;
    return res;
  } catch (e) {
    if (fromSites) {
      return e?.response;
    } else {
      const errorMessage = e?.response?.data?.error_description;
      console.error("Error adding user role:", e);
      handleApiError(
        "warning",
        errorMessage
          ? errorMessage
          : "Failed to add user role, please try again."
      );
      return e;
    }
  }
};

export const revokeRole = async (
  activeSession,
  userid,
  roleid,
  siteids = [],
  fromSites
) => {
  try {
    const response = await axiosInstance.post(revokeRoleEndPoint(), {
      accountname: activeSession.accountId,
      grant: {
        userid: userid,
        roleid: roleid,
        siteids: siteids,
      },
    });
    const res = response.data;
    return res;
  } catch (e) {
    if (fromSites) {
      return e?.response;
    } else {
      const errorMessage = e?.response?.data?.error_description;
      console.error("Error adding user role:", e);
      handleApiError(
        "warning",
        errorMessage
          ? errorMessage
          : "Failed to add user role, please try again."
      );
      return e;
    }
  }
};
export const getRoleTableData = (requestdefinitions) => {
  let uniqueActions = ["View", "Create", "Edit", "Delete", "Download"];
  let tableDataList = [];
  requestdefinitions.forEach((definition) => {
    let tableData = {
      key: definition.object,
      object: definition.object,
    };
    definition.actions.forEach((actionObj) => {
      tableData[actionObj.action] = {
        isenabled: actionObj.isenabled,
        ismutable: actionObj.ismutable,
      };
    });
    tableDataList.push(tableData);
  });
  return { uniqueActions, tableDataList };
};

export const getUsersFilteredBySiteAndRole = async (
  activeSession,
  roleids = [],
  siteids = []
) => {
  try {
    let selectionFilter = "";
    if (siteids.length > 0) {
      selectionFilter += siteids
        .map(siteid => `(rbacsiteid eq '${siteid}')`)
        .join(" OR ");
      selectionFilter += ` OR (rbacsiteid eq null)`;
    }
    if (roleids.length > 0) {
      if (selectionFilter.length > 0) selectionFilter += " and ";
      selectionFilter += roleids
        .map(roleid => `(rbacroleid eq '${roleid}')`)
        .join(" OR ");
    }

    // If no filters are provided, set it to return all users (no filters)
    if (selectionFilter.length === 0) {
      selectionFilter = "1 eq 1"; // This will return all users (no filter).
    }
    const response = await axiosInstance.post(getUsersList(), {
      accountname: activeSession.accountId,
      filter: {
        $selection: selectionFilter,
      },
    })
    const res = response?.data || [];
    return res;
  } catch (e) {
    throw Error(e);
  }
};
